import type { CSSProperties } from 'react';
import { PaletteColor, ColorValue, HexColor, StatusTone } from '../types';

/**
 * Shared palette for configured metric colors.
 * Chart components import these; CSS uses the custom properties set by
 * applyStatusColorCssVars() in src/index.tsx.
 */
export const STATUS_COLORS: Record<PaletteColor, string> = {
  green: '#10b981',
  yellow: '#f59e0b',
  red: '#ef4444',
  blue: '#3b82f6',
  teal: '#14b8a6',
  amber: '#f97316',
  gray: '#6b7280',
  cyan: '#06b6d4',
  sky: '#0ea5e9',
  indigo: '#6366f1',
  violet: '#8b5cf6',
  purple: '#a855f7',
  pink: '#ec4899',
  rose: '#f43f5e',
  lime: '#84cc16',
  slate: '#64748b',
};

/** Darker partner shades for gradients (MetricCard accent bars). */
export const STATUS_COLORS_DARK: Record<PaletteColor, string> = {
  green: '#059669',
  yellow: '#d97706',
  red: '#dc2626',
  blue: '#2563eb',
  teal: '#0d9488',
  amber: '#ea580c',
  gray: '#4b5563',
  cyan: '#0891b2',
  sky: '#0284c7',
  indigo: '#4f46e5',
  violet: '#7c3aed',
  purple: '#9333ea',
  pink: '#db2777',
  rose: '#e11d48',
  lime: '#65a30d',
  slate: '#475569',
};

/**
 * Text-safe shades for status text/labels shown on the light status tint
 * backgrounds (e.g. DataCard, comparison indicators, trend summaries).
 * STATUS_COLORS_DARK fails AA (< 4.5:1) against those tints; these pass
 * against both tint shades used across the app (e.g. #ecfdf5/#d1fae5).
 */
export const STATUS_COLORS_TEXT: Record<PaletteColor, string> = {
  green: '#047857',
  yellow: '#92400e',
  red: '#991b1b',
  blue: '#1d4ed8',
  teal: '#0f766e',
  amber: '#9a3412',
  gray: '#374151',
  cyan: '#0e7490',
  sky: '#0369a1',
  indigo: '#4338ca',
  violet: '#6d28d9',
  purple: '#7e22ce',
  pink: '#be185d',
  rose: '#be123c',
  lime: '#3f6212',
  slate: '#334155',
};

export const PALETTE: ReadonlyArray<{ key: PaletteColor; name: string }> = [
  'green', 'teal', 'cyan', 'sky', 'blue', 'indigo', 'violet', 'purple',
  'pink', 'rose', 'red', 'amber', 'yellow', 'lime', 'slate', 'gray',
].map(key => ({ key: key as PaletteColor, name: key[0].toUpperCase() + key.slice(1) }));

export interface ResolvedColor {
  base: string;
  dark: string;
  text: string;
  tint: string;
  tintStrong: string;
  border: string;
}

// These values shipped before the expanded palette. Keep saved cards unchanged.
const SAVED_PALETTE_APPEARANCES: Partial<Record<PaletteColor, {
  tint: string;
  tintStrong: string;
  border: string;
  cardBackground: string;
  cardShadow: string;
}>> = {
  green: {
    tint: '#ecfdf5', tintStrong: '#d1fae5', border: 'rgba(16, 185, 129, 0.2)',
    cardBackground: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
    cardShadow: '0 4px 16px rgba(16, 185, 129, 0.1)',
  },
  yellow: {
    tint: '#fffbeb', tintStrong: '#fef3c7', border: 'rgba(245, 158, 11, 0.2)',
    cardBackground: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.05) 100%)',
    cardShadow: '0 4px 16px rgba(245, 158, 11, 0.1)',
  },
  red: {
    tint: '#fef2f2', tintStrong: '#fecaca', border: 'rgba(239, 68, 68, 0.2)',
    cardBackground: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%)',
    cardShadow: '0 4px 16px rgba(239, 68, 68, 0.1)',
  },
  blue: {
    tint: '#eff6ff', tintStrong: '#dbeafe', border: 'rgba(59, 130, 246, 0.2)',
    cardBackground: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.05) 100%)',
    cardShadow: '0 4px 16px rgba(59, 130, 246, 0.1)',
  },
  teal: {
    tint: '#f0fdfa', tintStrong: '#ccfbf1', border: 'rgba(20, 184, 166, 0.2)',
    cardBackground: 'linear-gradient(135deg, rgba(20, 184, 166, 0.1) 0%, rgba(13, 148, 136, 0.05) 100%)',
    cardShadow: '0 4px 16px rgba(20, 184, 166, 0.1)',
  },
  amber: {
    tint: '#fff7ed', tintStrong: '#ffedd5', border: 'rgba(249, 115, 22, 0.2)',
    cardBackground: 'linear-gradient(135deg, rgba(249, 115, 22, 0.1) 0%, rgba(234, 88, 12, 0.05) 100%)',
    cardShadow: '0 4px 16px rgba(249, 115, 22, 0.1)',
  },
  gray: {
    tint: '#f9fafb', tintStrong: '#e5e7eb', border: 'rgba(107, 114, 128, 0.2)',
    cardBackground: 'linear-gradient(135deg, rgba(107, 114, 128, 0.1) 0%, rgba(75, 85, 99, 0.05) 100%)',
    cardShadow: '0 4px 16px rgba(107, 114, 128, 0.1)',
  },
};

export function isHexColor(value: string): value is HexColor {
  return /^#[0-9a-fA-F]{6}$/.test(value);
}

function channels(hex: string): number[] {
  return [1, 3, 5].map(start => parseInt(hex.slice(start, start + 2), 16));
}

function mix(hex: string, amount: number, toward: number): string {
  return '#' + channels(hex).map(channel =>
    Math.round(channel * (1 - amount) + toward * amount).toString(16).padStart(2, '0')
  ).join('');
}

function luminance(hex: string): number {
  const linear = channels(hex).map(channel => {
    const c = channel / 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return linear[0] * 0.2126 + linear[1] * 0.7152 + linear[2] * 0.0722;
}

function contrast(a: string, b: string): number {
  const first = luminance(a);
  const second = luminance(b);
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}

export function resolveColor(value: ColorValue | null | undefined, fallback: PaletteColor = 'gray'): ResolvedColor {
  const candidate = value == null ? fallback : value;
  const palette = Object.prototype.hasOwnProperty.call(STATUS_COLORS, candidate)
    ? candidate as PaletteColor
    : undefined;
  if (!palette && !isHexColor(candidate)) return resolveColor('gray');

  const base = palette ? STATUS_COLORS[palette] : candidate;
  const saved = palette ? SAVED_PALETTE_APPEARANCES[palette] : undefined;
  const tint = saved?.tint ?? mix(base, 0.9, 255);
  const tintStrong = saved?.tintStrong ?? mix(base, 0.82, 255);
  const border = saved?.border ?? mix(base, 0.65, 255);
  const dark = palette ? STATUS_COLORS_DARK[palette] : mix(base, 0.2, 0);
  let text = palette ? STATUS_COLORS_TEXT[palette] : base;
  if (!palette) {
    for (let step = 0; step <= 20; step++) {
      text = mix(base, step * 0.05, 0);
      if (contrast(text, tintStrong) >= 4.5) break;
    }
  }
  return { base, dark, text, tint, tintStrong, border };
}

export function colorCssVars(value: ColorValue): CSSProperties {
  const color = resolveColor(value);
  const saved = Object.prototype.hasOwnProperty.call(SAVED_PALETTE_APPEARANCES, value)
    ? SAVED_PALETTE_APPEARANCES[value as PaletteColor]
    : undefined;
  return {
    '--status-base': color.base,
    '--status-dark': color.dark,
    '--status-text': color.text,
    '--status-tint': color.tint,
    '--status-tint-strong': color.tintStrong,
    '--status-border': color.border,
    ...(saved ? { '--status-card-background': saved.cardBackground, '--status-card-shadow': saved.cardShadow } : {}),
  } as CSSProperties;
}

/** Compatibility for old callers that do not yet pass a tone. Dashboard always does. */
export function legacyStatusTone(color: ColorValue): StatusTone {
  switch (color) {
    case 'green': return 'positive';
    case 'blue':
    case 'teal':
    case 'gray': return 'neutral';
    case 'red': return 'concern';
    default: return 'caution';
  }
}

export function toneIndicatorVariant(tone: StatusTone): 'above' | 'typical' | 'below' {
  return tone === 'positive' ? 'above' : tone === 'neutral' ? 'typical' : 'below';
}

export const TONE_FALLBACK_LABELS: Record<StatusTone, string> = {
  positive: 'In range',
  neutral: 'Typical',
  caution: 'Close to range',
  concern: 'Needs attention',
};

/**
 * The status label a metric card hands to its chart.
 *
 * A label that was actually set (an admin threshold label, or a norms band
 * label) always wins. Otherwise the fallback word is used - EXCEPT in
 * single-color mode. There, color is not a status signal, and the admin screen
 * promises the saved threshold ranges are not used, so a word derived from them
 * would be false. Returning undefined lets each chart fall back to its own
 * single-color text instead ("Tracking", or a comparison with the target).
 */
export function resolveCardStatusLabel(
  statusLabel: string | undefined,
  tone: StatusTone,
  customColor: string | undefined
): string | undefined {
  if (statusLabel) return statusLabel;
  if (customColor) return undefined;
  return TONE_FALLBACK_LABELS[tone];
}

/** Threshold ranges as the charts receive them, plus the admin's range colors. */
export interface ThresholdBarRanges {
  greenMin?: number | null;
  greenMax?: number | null;
  yellowMin?: number | null;
  yellowMax?: number | null;
  /** Admin color for the Good range; null/undefined means green. */
  goodColor?: ColorValue | null;
  /** Admin color for the Attention range; null/undefined means yellow. */
  attentionColor?: ColorValue | null;
  /** Admin color for the Concern range; null/undefined means red. */
  concernColor?: ColorValue | null;
}

/**
 * The color of one history bar under threshold coloring. The range tests are
 * the ones both time-series charts used before; only the colors are now the
 * admin's picks.
 */
export function thresholdBarColor(value: number, thresholds?: ThresholdBarRanges, fallback: ColorValue = 'green'): string {
  if (!thresholds) return getStatusColor(fallback);
  const { greenMin, greenMax, yellowMin, yellowMax } = thresholds;
  if (greenMin != null && greenMax != null && value >= greenMin && value <= greenMax) {
    return getStatusColor(thresholds.goodColor || 'green');
  }
  if (yellowMin != null && yellowMax != null && value >= yellowMin && value <= yellowMax) {
    return getStatusColor(thresholds.attentionColor || 'yellow');
  }
  const hasAnyThreshold = greenMin != null || greenMax != null || yellowMin != null || yellowMax != null;
  if (!hasAnyThreshold) return getStatusColor(fallback);
  return getStatusColor(thresholds.concernColor || 'red');
}

export function getStatusColor(value: ColorValue | null | undefined): string {
  return resolveColor(value).base;
}

export function applyStatusColorCssVars(root: HTMLElement = document.documentElement): void {
  (Object.keys(STATUS_COLORS) as PaletteColor[]).forEach((s) => {
    root.style.setProperty(`--status-${s}`, STATUS_COLORS[s]);
    root.style.setProperty(`--status-${s}-dark`, STATUS_COLORS_DARK[s]);
    root.style.setProperty(`--status-${s}-text`, STATUS_COLORS_TEXT[s]);
  });
}
